import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const OrderHistoryPageSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e0e0e0">
      <Box
        sx={{
          minHeight: '100vh',
          background: '#f8fafc',
        }}
      >
        <Box
          sx={{
            background: '#ffffff',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            borderRadius: '0 0 32px 32px',
            p: { xs: 2, sm: 3, md: 5 },
            mb: { xs: 2, md: 4 },
          }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            mb: 3
          }}>
            <Skeleton circle width={isMobile ? 48 : 60} height={isMobile ? 48 : 60} />
            <Box sx={{ flex: 1 }}>
              <Skeleton height={isMobile ? 28 : 40} width={isMobile ? 160 : 220} style={{ marginBottom: 8 }} />
              <Skeleton height={isMobile ? 16 : 20} width={isMobile ? '100%' : 320} />
            </Box>
          </Box>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            <Box sx={{ flex: 1 }}>
              <Skeleton height={isMobile ? 48 : 56} width="100%" style={{ borderRadius: '12px' }} />
            </Box>
            <Skeleton height={isMobile ? 48 : 56} width={isMobile ? '100%' : 120} style={{ borderRadius: '12px' }} />
          </Box>
        </Box>

        <Box sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 4 } }}>
            {[...Array(4)].map((_, index) => (
              <Grid item xs={6} sm={6} md={3} key={index}>
                <Card sx={{
                  borderRadius: '20px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                }}>
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, mb: 2 }}>
                      <Skeleton circle width={isMobile ? 40 : 50} height={isMobile ? 40 : 50} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Skeleton height={isMobile ? 24 : 32} width={isMobile ? 50 : 80} style={{ marginBottom: 4 }} />
                        <Skeleton height={isMobile ? 12 : 16} width={isMobile ? '100%' : 120} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{
            borderRadius: '20px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden'
          }}>
            {isMobile ? (
              <Box sx={{ p: 2 }}>
                {[...Array(5)].map((_, index) => (
                  <Card
                    key={index}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: 2,
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Skeleton height={16} width={80} />
                      <Skeleton height={20} width={60} style={{ borderRadius: 8 }} />
                    </Box>
                    <Skeleton height={14} width="60%" style={{ marginBottom: 4 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Skeleton height={14} width={60} />
                      <Skeleton circle width={32} height={32} />
                    </Box>
                  </Card>
                ))}
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell><Skeleton height={20} width={80} /></TableCell>
                      <TableCell><Skeleton height={20} width={100} /></TableCell>
                      {!isTablet && <TableCell><Skeleton height={20} width={80} /></TableCell>}
                      <TableCell><Skeleton height={20} width={isTablet ? 100 : 150} /></TableCell>
                      <TableCell><Skeleton height={20} width={80} /></TableCell>
                      <TableCell><Skeleton height={20} width={80} /></TableCell>
                      {!isTablet && <TableCell><Skeleton height={20} width={100} /></TableCell>}
                      <TableCell align="right"><Skeleton height={20} width={80} /></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(10)].map((_, index) => (
                      <TableRow
                        key={index}
                        sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' } }}
                      >
                        <TableCell><Skeleton height={20} width={100} /></TableCell>
                        <TableCell><Skeleton height={20} width={120} /></TableCell>
                        {!isTablet && <TableCell><Skeleton height={20} width={80} /></TableCell>}
                        <TableCell><Skeleton height={16} width={isTablet ? 100 : 200} /></TableCell>
                        <TableCell><Skeleton height={20} width={80} /></TableCell>
                        <TableCell><Skeleton height={24} width={90} style={{ borderRadius: '12px' }} /></TableCell>
                        {!isTablet && <TableCell><Skeleton height={16} width={140} /></TableCell>}
                        <TableCell align="right"><Skeleton circle width={36} height={36} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{
              p: 2,
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              display: 'flex',
              justifyContent: { xs: 'center', md: 'flex-end' }
            }}>
              <Skeleton height={40} width={isMobile ? 200 : 300} />
            </Box>
          </Paper>
        </Box>
      </Box>
    </SkeletonTheme>
  );
};

export default OrderHistoryPageSkeleton;
